import { useMemo, useState } from 'react'
import { Money } from '@klear/money'
import { PAAS_SETTLEMENT_CURRENCY } from '@klear/paas/constants'
import { PAYMENT_ACCOUNT_TYPE_KLEAR_FINANCIAL_ACCOUNT, PAYMENT_NETWORK_KLEAR_TO_KLEAR } from '@klear/payment/constants'
import type { PaymentAccount } from '@klear/payment/payment-account'
import { Button } from '@klear/ui/components/button'
import { Combobox, type ComboboxOption } from '@klear/ui/components/combobox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@klear/ui/components/dialog'
import { Label } from '@klear/ui/components/label'
import { MoneyInput } from '@klear/ui/components/money-input'
import { UtcDateNotice } from '@klear/ui/components/utc-date-notice'
import { useFunderPayment } from '@klear/ui/hooks/use-funder-payment'
import { usePaasFunders, usePaasMutations, type PaasDisbursableRow } from '@klear/ui/hooks/use-paas'

type AccountSelection = { type: string; id: number; organizationId: number }

export default function FundPaasDialog({
  disbursable,
  onOpenChange
}: {
  disbursable: PaasDisbursableRow
  onOpenChange: (open: boolean) => void
}) {
  const { createAdvance, isCreatingAdvance } = usePaasMutations()
  const { funders, isLoading: isFundersLoading } = usePaasFunders()
  const [funderOrganizationId, setFunderOrganizationId] = useState<number | null>(null)
  const [source, setSource] = useState<AccountSelection | null>(null)
  const [destination, setDestination] = useState<AccountSelection | null>(null)
  const [error, setError] = useState<string | null>(null)

  const { sourcePaymentAccounts, destinationPaymentAccounts, isSourcePaymentAccountsLoading } = useFunderPayment({
    sourceId: funderOrganizationId ?? undefined,
    destinationId: disbursable.traderOrganizationId
  })

  const toOptions = (accounts: PaymentAccount[]): ComboboxOption<AccountSelection>[] =>
    accounts
      .filter((account) => account.type === PAYMENT_ACCOUNT_TYPE_KLEAR_FINANCIAL_ACCOUNT)
      .map((account) => ({
        value: { type: account.type, id: account.id, organizationId: account.organizationId },
        label: `${account.name} (${account.last4})`
      }))

  const funderOptions = useMemo<ComboboxOption<number>[]>(
    () =>
      funders.map((funder) => ({
        value: funder.organizationId,
        label: funder.organizationName || `Funder ${funder.funderId}`
      })),
    [funders]
  )
  const sourceOptions = useMemo(() => toOptions(sourcePaymentAccounts), [sourcePaymentAccounts])
  const destinationOptions = useMemo(() => toOptions(destinationPaymentAccounts), [destinationPaymentAccounts])

  const isForeignCurrency = disbursable.payableAmount.currency !== PAAS_SETTLEMENT_CURRENCY
  const [amount, setAmount] = useState<Money>(
    isForeignCurrency ? Money.zero(PAAS_SETTLEMENT_CURRENCY) : disbursable.payableAmount
  )

  const onFunderChange = (next: number | '') => {
    setFunderOrganizationId(next === '' ? null : next)
    setSource(null)
    setError(null)
  }

  const sourcePlaceholder = (): string => {
    if (funderOrganizationId == null) return 'Select an SPV first'
    if (isSourcePaymentAccountsLoading) return 'Loading…'
    if (sourceOptions.length === 0) return 'This SPV has no Klear account'
    return 'Select funding account'
  }

  const onSubmit = async () => {
    if (funderOrganizationId == null) {
      setError('Choose the SPV funding this advance.')
      return
    }
    if (source == null || destination == null) {
      setError('Choose the funding and receiving accounts.')
      return
    }
    if (!amount.isPositive()) {
      setError(`Enter the ${PAAS_SETTLEMENT_CURRENCY} amount going out.`)
      return
    }
    setError(null)
    await createAdvance({
      allocations: [{ supplierPaymentId: disbursable.supplierPaymentId, amount: amount.toJSON() }],
      source,
      destination,
      paymentNetwork: PAYMENT_NETWORK_KLEAR_TO_KLEAR
    })
    setSource(null)
    setDestination(null)
    onOpenChange(false)
  }

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Fund as PaaS</DialogTitle>
          <DialogDescription>
            {`Advance ${disbursable.traderOrganizationName} against ${disbursable.supplierPaymentIdDisplay}` +
              (disbursable.supplierOrganizationName ? ` to ${disbursable.supplierOrganizationName}` : '') +
              `. Saved as a draft — disburse it when it is time.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {isForeignCurrency ? (
            <MoneyInput
              label={`Advance amount (${PAAS_SETTLEMENT_CURRENCY})`}
              value={amount}
              onChange={setAmount}
              currencyDisabled
              helperText={`The trader owes their supplier ${disbursable.payableAmount.toString()}. Klear can only send ${PAAS_SETTLEMENT_CURRENCY}, and the rate is not known until it settles — enter the dollars going out. That figure is what the trader will owe back.`}
            />
          ) : (
            <div className="rounded-lg border border-border p-3">
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-muted-foreground">Advance amount</span>
                <span className="text-lg font-semibold">{amount.toString()}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">The full payable. PaaS funds a payable in full.</p>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>SPV</Label>
            <Combobox
              options={funderOptions}
              value={funderOrganizationId ?? ''}
              valueType="number"
              onChange={onFunderChange}
              placeholder={
                isFundersLoading ? 'Loading…' : funderOptions.length === 0 ? 'No funders available' : 'Select SPV'
              }
            />
            <p className="text-xs text-muted-foreground">
              The capital provider. Its accounts are what the funding list below offers.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>Funding account</Label>
            <Combobox
              options={sourceOptions}
              value={source ?? ''}
              valueType="object"
              onChange={(next) => setSource(next === '' ? null : next)}
              placeholder={sourcePlaceholder()}
              disabled={funderOrganizationId == null}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Receiving account ({disbursable.traderOrganizationName})</Label>
            <Combobox
              options={destinationOptions}
              value={destination ?? ''}
              valueType="object"
              onChange={(next) => setDestination(next === '' ? null : next)}
              placeholder={
                destinationOptions.length === 0 ? 'This trader has no Klear account' : "Select the trader's account"
              }
            />
          </div>

          {error != null && <p className="text-sm text-destructive">{error}</p>}

          <UtcDateNotice />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isCreatingAdvance}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={isCreatingAdvance || funderOrganizationId == null}>
            {isCreatingAdvance ? 'Creating…' : 'Create draft advance'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
